import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '@/api/topics';

export const TOPICS_KEY = ['topics'] as const;
export const TOPIC_KEY = (id: string) => ['topic', id] as const;

export function useTopics(params: Parameters<typeof api.listTopics>[0] = {}) {
  return useQuery({ queryKey: [...TOPICS_KEY, params], queryFn: () => api.listTopics(params) });
}

export function useTopic(id: string | null) {
  return useQuery({
    queryKey: TOPIC_KEY(id ?? ''),
    queryFn: () => api.getTopic(id!),
    enabled: !!id,
  });
}

export function useCreateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: api.CreateTopicInput) => api.createTopic(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOPICS_KEY }),
  });
}

export function useUpdateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<api.CreateTopicInput> }) =>
      api.updateTopic(id, input),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: TOPICS_KEY });
      qc.invalidateQueries({ queryKey: TOPIC_KEY(vars.id) });
    },
  });
}

export function useDeleteTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteTopic(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOPICS_KEY }),
  });
}
